import React, { useEffect, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import Modal from "react-modal";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../css/datepicker.css";

const BACKEND_URL = "https://linxlegacyacademy.dk";

// Kategori → farve (bruges hvis event ikke har egen farve)
const CATEGORY_COLORS = {
  Træning:    "#8b5cf6",
  Kamp:       "#f43f5e",
  Turnering:  "#06b6d4",
  Møde:       "#10b981",
  Andet:      "#f59e0b",
};

const TOURNAMENT_STATUSES = ["upcoming", "open", "live", "closed"];

export default function AdminSide() {
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [products, setProducts] = useState([]);
  const [newTeam, setNewTeam] = useState("");
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("players"); // players | teams | calendar | tournaments

  // Kalender
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [events, setEvents] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState({});

  // Turneringer
  const [tournaments, setTournaments] = useState([]);
  const [tModalOpen, setTModalOpen] = useState(false);
  const [tModalData, setTModalData] = useState({});
  const [participants, setParticipants] = useState([]);
  const [showParticipants, setShowParticipants] = useState(null);

  // =============================
  // FETCH INITIAL DATA
  // =============================
  useEffect(() => {
    Promise.all([
      fetch(`${BACKEND_URL}/api/admin/users`, { credentials: "include" }).then(r => r.json()),
      fetch(`${BACKEND_URL}/api/admin/stripe-products`, { credentials: "include" }).then(r => r.json()),
      fetch(`${BACKEND_URL}/api/admin/teams`, { credentials: "include" }).then(r => r.json())
    ])
      .then(([userData, stripeData, teamData]) => {
        const usersList = userData?.users || [];
        setUsers(usersList);
        setProducts(Array.isArray(stripeData) ? stripeData : []);
        const safeTeams = Array.isArray(teamData) ? teamData : teamData?.teams || [];
        setTeams(safeTeams.map(t => ({
          ...t,
          player_count: usersList.filter(u => (u.team_ids || []).includes(t.id)).length
        })));
      })
      .finally(() => setLoading(false));
  }, []);

  // Turneringer
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/admin/tournaments`, { credentials: "include" })
      .then(r => r.json())
      .then(data => setTournaments(Array.isArray(data) ? data : []))
      .catch(() => setTournaments([]));
  }, []);

  // Events for valgt hold
  useEffect(() => {
    if (!selectedTeam) return;
    fetch(`${BACKEND_URL}/api/admin/teams/${selectedTeam}/events`, { credentials: "include" })
      .then(res => res.json())
      .then(data => setEvents(Array.isArray(data) ? data : []))
      .catch(() => setEvents([]));
  }, [selectedTeam]);

  // =============================
  // USER FUNCTIONS
  // =============================
  const updateMembership = async (userId, priceId) => {
    await fetch(`${BACKEND_URL}/api/admin/users/${userId}/membership`, {
      method: "PUT", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ price_id: priceId })
    });
    setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, stripe_price_id: priceId } : u));
  };

  // Multi-hold: toggle et hold på/af for en bruger
  const toggleUserTeam = async (userId, teamId) => {
    const user = users.find(u => u.user_id === userId);
    const current = user.team_ids || [];
    const next = current.includes(teamId)
      ? current.filter(id => id !== teamId)
      : [...current, teamId];

    await fetch(`${BACKEND_URL}/api/admin/users/${userId}/teams`, {
      method: "PUT", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ team_ids: next })
    });

    setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, team_ids: next } : u));
    setTeams(prev => prev.map(t => ({
      ...t,
      player_count: users
        .map(u => u.user_id === userId ? { ...u, team_ids: next } : u)
        .filter(u => (u.team_ids || []).includes(t.id)).length
    })));
  };

  // =============================
  // TEAM FUNCTIONS
  // =============================
  const createTeam = async () => {
    if (!newTeam.trim()) return;
    const res = await fetch(`${BACKEND_URL}/api/admin/teams`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: newTeam })
    });
    const data = await res.json();
    setTeams(prev => [...prev, { ...data, player_count: 0 }]);
    setNewTeam("");
  };

  const updateTeamName = async (teamId, name) => {
    await fetch(`${BACKEND_URL}/api/admin/teams/${teamId}`, {
      method: "PUT", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name })
    });
    setTeams(prev => prev.map(t => t.id === teamId ? { ...t, name } : t));
  };

  const deleteTeam = async (teamId) => {
    if (!window.confirm("Slet holdet?")) return;
    await fetch(`${BACKEND_URL}/api/admin/teams/${teamId}`, { method: "DELETE", credentials: "include" });
    setTeams(prev => prev.filter(t => t.id !== teamId));
    setUsers(prev => prev.map(u => ({ ...u, team_ids: (u.team_ids || []).filter(id => id !== teamId) })));
  };

  // =============================
  // EVENT MODAL
  // =============================
  const openCreateEventModal = (selectInfo) => {
    const start = selectInfo.start ? new Date(selectInfo.start) : new Date();
    const end = selectInfo.end ? new Date(selectInfo.end) : start;
    setModalData({ title: "", start_time: start, end_time: end, team_id: selectedTeam,
      category: "Træning", link: "", spil: "", consol: "", color: "", description: "", all_day: false });
    setModalOpen(true);
  };

  const openEditEventModal = (event) => {
    const start = event.start ? new Date(event.start) : new Date();
    const end = event.end ? new Date(event.end) : start;
    setModalData({
      id: event.id, title: event.title, start_time: start, end_time: end, team_id: selectedTeam,
      category: event.extendedProps.category || "Træning",
      link: event.extendedProps.link || "", spil: event.extendedProps.spil || "",
      consol: event.extendedProps.consol || "", color: event.extendedProps.color || "",
      description: event.extendedProps.description || "",
      all_day: event.extendedProps.all_day || false
    });
    setModalOpen(true);
  };

  const saveEvent = async () => {
    if (!modalData.title) return alert("Titel er påkrævet");
    if (!selectedTeam) return alert("Vælg et hold først");
    try {
      const payload = {
        ...modalData,
        start_time: modalData.start_time ? new Date(modalData.start_time).toISOString() : null,
        end_time: modalData.end_time ? new Date(modalData.end_time).toISOString() : null
      };
      const res = modalData.id
        ? await fetch(`${BACKEND_URL}/api/admin/events/${modalData.id}`, {
            method: "PUT", credentials: "include",
            headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) })
        : await fetch(`${BACKEND_URL}/api/admin/teams/${selectedTeam}/events`, {
            method: "POST", credentials: "include",
            headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      if (!res.ok) throw new Error(`Backend fejl: ${res.status}`);
      const savedEvent = await res.json();
      setEvents(prev => modalData.id ? prev.map(e => e.id === savedEvent.id ? savedEvent : e) : [...prev, savedEvent]);
      setModalOpen(false);
    } catch (err) {
      console.error("Save event failed:", err);
      alert("Der skete en fejl. Se konsol.");
    }
  };

  const deleteEvent = async () => {
    if (!modalData.id) return;
    await fetch(`${BACKEND_URL}/api/admin/events/${modalData.id}`, { method: "DELETE", credentials: "include" });
    setEvents(prev => prev.filter(e => e.id !== modalData.id));
    setModalOpen(false);
  };

  const updateEventTime = async (info) => {
    const payload = {
      start_time: info.event.start ? new Date(info.event.start).toISOString() : null,
      end_time: info.event.end ? new Date(info.event.end).toISOString() : null
    };
    const res = await fetch(`${BACKEND_URL}/api/admin/events/${info.event.id}`, {
      method: "PUT", credentials: "include",
      headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
    if (!res.ok) return;
    const updated = await res.json();
    setEvents(prev => prev.map(ev => ev.id === updated.id ? updated : ev));
  };

  // =============================
  // TOURNAMENT FUNCTIONS
  // =============================
  const openCreateTournament = () => {
    setTModalData({
      name: "", game: "CS2", status: "upcoming", date: new Date(),
      format: "Single Elimination", max_participants: 16, fee: 20,
      challengermode_url: "", prize_1: "", prize_2: "", prize_3: ""
    });
    setTModalOpen(true);
  };

  const openEditTournament = (t) => {
    setTModalData({ ...t, date: t.date ? new Date(t.date) : new Date() });
    setTModalOpen(true);
  };

  const saveTournament = async () => {
    if (!tModalData.name) return alert("Navn er påkrævet");
    const payload = {
      ...tModalData,
      date: tModalData.date ? new Date(tModalData.date).toISOString() : null,
      max_participants: tModalData.max_participants ? Number(tModalData.max_participants) : null,
      fee: Number(tModalData.fee || 0),
    };
    const res = tModalData.id
      ? await fetch(`${BACKEND_URL}/api/admin/tournaments/${tModalData.id}`, {
          method: "PUT", credentials: "include",
          headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) })
      : await fetch(`${BACKEND_URL}/api/admin/tournaments`, {
          method: "POST", credentials: "include",
          headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
    if (!res.ok) { alert("Fejl ved gem"); return; }
    // Genindlæs liste
    const list = await fetch(`${BACKEND_URL}/api/admin/tournaments`, { credentials: "include" }).then(r => r.json());
    setTournaments(Array.isArray(list) ? list : []);
    setTModalOpen(false);
  };

  const deleteTournament = async (id) => {
    await fetch(`${BACKEND_URL}/api/admin/tournaments/${id}`, { method: "DELETE", credentials: "include" });
    setTournaments(prev => prev.filter(t => t.id !== id));
  };

  // Inline navn-redigering
  const updateTournamentName = async (id, name) => {
    setTournaments(prev => prev.map(t => t.id === id ? { ...t, name } : t));
    await fetch(`${BACKEND_URL}/api/admin/tournaments/${id}`, {
      method: "PUT", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name })
    });
  };

  const viewParticipants = async (id) => {
    const data = await fetch(`${BACKEND_URL}/api/admin/tournaments/${id}/participants`, { credentials: "include" }).then(r => r.json());
    setParticipants(Array.isArray(data) ? data : []);
    setShowParticipants(id);
  };

  if (loading) return <p style={{ padding: 20 }}>Indlæser...</p>;

  const filteredUsers = users.filter(u =>
    u.username.toLowerCase().includes(search.toLowerCase()) ||
    u.user_id.toString().includes(search)
  );

  const tabBtn = (key, label) => (
    <button
      onClick={() => setTab(key)}
      style={{
        padding: "10px 18px", border: "none", cursor: "pointer",
        background: tab === key ? "#e8b43a" : "#1c2030",
        color: tab === key ? "#0d0f14" : "#e8eaf0",
        fontWeight: 600, borderRadius: 8, fontSize: 14
      }}
    >{label}</button>
  );

  return (
    <div style={{ padding: "20px", background: "#0d0f14", minHeight: "100vh", color: "#e8eaf0" }}>
      <div style={{ display: "flex", gap: 10, marginBottom: 30, flexWrap: "wrap" }}>
        {tabBtn("players", "Spillere")}
        {tabBtn("teams", "Hold")}
        {tabBtn("calendar", "Kalender")}
        {tabBtn("tournaments", "Turneringer")}
      </div>

      {/* ───────── SPILLERE ───────── */}
      {tab === "players" && (
        <>
          <h1 style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
            Spillere
            <input type="text" placeholder="Søg ID eller navn..."
              value={search} onChange={e => setSearch(e.target.value)}
              style={{ padding: 8, fontSize: 14, borderRadius: 6, border: "1px solid #333", background: "#14171f", color: "#fff" }} />
          </h1>
          <table style={{ borderCollapse: "collapse", width: "100%", marginTop: 16 }}>
            <thead>
              <tr style={{ background: "#1c2030" }}>
                <th style={th}>ID</th><th style={th}>Navn</th><th style={th}>Email</th>
                <th style={th}>Pakke</th><th style={th}>Hold (flere muligt)</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(user => (
                <tr key={user.user_id} style={{ borderBottom: "1px solid #222" }}>
                  <td style={td}>{user.user_id}</td>
                  <td style={td}>{user.username}</td>
                  <td style={td}>{user.email}</td>
                  <td style={td}>
                    <select value={user.stripe_price_id || ""} onChange={e => updateMembership(user.user_id, e.target.value)} style={sel}>
                      <option value="">Ingen</option>
                      {products.map(p => (
                        <option key={p.price_id} value={p.price_id}>{p.product_name} – {p.amount} kr</option>
                      ))}
                    </select>
                  </td>
                  <td style={td}>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {teams.map(team => {
                        const active = (user.team_ids || []).includes(team.id);
                        return (
                          <button key={team.id} onClick={() => toggleUserTeam(user.user_id, team.id)}
                            style={{
                              padding: "4px 10px", borderRadius: 20, fontSize: 12, cursor: "pointer",
                              border: active ? "1px solid #22c55e" : "1px solid #333",
                              background: active ? "#22c55e22" : "transparent",
                              color: active ? "#22c55e" : "#8b92a8"
                            }}>
                            {active ? "✓ " : ""}{team.name}
                          </button>
                        );
                      })}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {/* ───────── HOLD ───────── */}
      {tab === "teams" && (
        <>
          <h1>Hold</h1>
          <div style={{ marginBottom: 20, display: "flex", gap: 10 }}>
            <input type="text" placeholder="Nyt hold navn" value={newTeam}
              onChange={e => setNewTeam(e.target.value)}
              style={{ padding: 8, borderRadius: 6, border: "1px solid #333", background: "#14171f", color: "#fff" }} />
            <button onClick={createTeam} style={btnPrimary}>Opret hold</button>
          </div>
          <table style={{ borderCollapse: "collapse", width: "100%" }}>
            <thead>
              <tr style={{ background: "#1c2030" }}>
                <th style={th}>ID</th><th style={th}>Navn</th><th style={th}>Spillere</th><th style={th}>Handling</th>
              </tr>
            </thead>
            <tbody>
              {teams.map(team => (
                <tr key={team.id} style={{ borderBottom: "1px solid #222" }}>
                  <td style={td}>{team.id}</td>
                  <td style={td}>
                    <input value={team.name} onChange={e => updateTeamName(team.id, e.target.value)}
                      style={{ padding: 6, borderRadius: 6, border: "1px solid #333", background: "#14171f", color: "#fff" }} />
                  </td>
                  <td style={td}><strong>{team.player_count}</strong></td>
                  <td style={td}><button onClick={() => deleteTeam(team.id)} style={btnDanger}>Slet</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {/* ───────── KALENDER ───────── */}
      {tab === "calendar" && (
        <>
          <h1>Kalender per hold</h1>
          <select value={selectedTeam || ""} onChange={e => setSelectedTeam(Number(e.target.value))}
            style={{ ...sel, marginBottom: 20, padding: 10 }}>
            <option value="">Vælg hold</option>
            {teams.map(team => (
              <option key={team.id} value={team.id}>{team.name} ({team.player_count} spillere)</option>
            ))}
          </select>

          {/* Farveforklaring */}
          {selectedTeam && (
            <div style={{ display: "flex", gap: 16, marginBottom: 16, flexWrap: "wrap" }}>
              {Object.entries(CATEGORY_COLORS).map(([cat, col]) => (
                <span key={cat} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
                  <span style={{ width: 12, height: 12, borderRadius: 3, background: col }} /> {cat}
                </span>
              ))}
            </div>
          )}

          {selectedTeam && (
            <div style={{ borderRadius: 10 }}>
              <FullCalendar
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                initialView="dayGridMonth"
                headerToolbar={{ left: "prev,next today", center: "title", right: "dayGridMonth,timeGridWeek,timeGridDay" }}
                slotMinTime="08:00:00"
                slotMaxTime="23:00:00"
                locale="da"
                firstDay={1}
                height="auto"
                expandRows={true}
                dayMaxEvents={3}
                selectable={true}
                selectMirror={true}
                unselectAuto={true}
                editable={true}
                nowIndicator={true}
                fixedWeekCount={false}
                navLinks={true}
                events={events.map(ev => ({
                  id: ev.id, title: ev.title, start: ev.start_time, end: ev.end_time,
                  allDay: ev.all_day || false,
                  backgroundColor: ev.color || CATEGORY_COLORS[ev.category] || "#4f8ef7",
                  borderColor: ev.color || CATEGORY_COLORS[ev.category] || "#4f8ef7",
                  extendedProps: { category: ev.category, link: ev.link, spil: ev.spil, consol: ev.consol, color: ev.color, description: ev.description, all_day: ev.all_day }
                }))}
                select={openCreateEventModal}
                dateClick={(info) => openCreateEventModal({ start: info.date, end: info.date })}
                eventClick={info => openEditEventModal(info.event)}
                eventDrop={updateEventTime}
                eventResize={updateEventTime}
              />
            </div>
          )}
        </>
      )}

      {/* ───────── TURNERINGER ───────── */}
      {tab === "tournaments" && (
        <>
          <h1 style={{ display: "flex", alignItems: "center", gap: 16 }}>
            Turneringer
            <button onClick={openCreateTournament} style={btnPrimary}>+ Ny turnering</button>
          </h1>
          <table style={{ borderCollapse: "collapse", width: "100%", marginTop: 16 }}>
            <thead>
              <tr style={{ background: "#1c2030" }}>
                <th style={th}>Navn</th><th style={th}>Spil</th><th style={th}>Status</th>
                <th style={th}>Dato</th><th style={th}>Gebyr</th><th style={th}>Tilmeldte</th><th style={th}>Handling</th>
              </tr>
            </thead>
            <tbody>
              {tournaments.map(t => (
                <tr key={t.id} style={{ borderBottom: "1px solid #222" }}>
                  <td style={td}>
                    <input
                      value={t.name}
                      onChange={e => updateTournamentName(t.id, e.target.value)}
                      style={{ padding: 6, borderRadius: 6, border: "1px solid #333", background: "#14171f", color: "#fff", width: "100%" }}
                    />
                  </td>
                  <td style={td}>{t.game}</td>
                  <td style={td}>{t.status}</td>
                  <td style={td}>{t.date ? new Date(t.date).toLocaleString("da-DK") : "-"}</td>
                  <td style={td}>{t.fee} kr</td>
                  <td style={td}>
                    <button onClick={() => viewParticipants(t.id)} style={{ ...btnGhost, fontSize: 12 }}>
                      {t.participant_count} 👁
                    </button>
                  </td>
                  <td style={td}>
                    <button onClick={() => openEditTournament(t)} style={{ ...btnGhost, marginRight: 6 }}>Rediger</button>
                    <button onClick={() => deleteTournament(t.id)} style={btnDanger}>Slet</button>
                  </td>
                </tr>
              ))}
              {tournaments.length === 0 && (
                <tr><td style={td} colSpan={7}>Ingen turneringer endnu</td></tr>
              )}
            </tbody>
          </table>
        </>
      )}

      {/* ───────── EVENT MODAL ───────── */}
      <Modal isOpen={modalOpen} onRequestClose={() => setModalOpen(false)} ariaHideApp={false}
        style={{
          overlay: { background: "rgba(0,0,0,.7)", zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center" },
          content: {
            position: "relative", inset: "auto", maxWidth: 580, width: "90%",
            background: "#14171f", border: "1px solid #333", borderRadius: 12,
            color: "#fff", padding: 24, maxHeight: "90vh", overflowY: "auto"
          }
        }}>
        <h2>{modalData.id ? "Rediger Event" : "Opret Event"}</h2>
        <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 16 }}>
          <div>
            <label style={{ fontSize: 12, color: "#8b92a8", display: "block", marginBottom: 4 }}>Titel</label>
            <input placeholder="Fx Holdtræning" value={modalData.title || ""} onChange={e => setModalData({ ...modalData, title: e.target.value })} style={{ ...inp, width: "100%" }} />
          </div>
          <div>
            <label style={{ fontSize: 12, color: "#8b92a8", display: "block", marginBottom: 4 }}>Kategori</label>
            <select value={modalData.category || "Træning"} onChange={e => setModalData({ ...modalData, category: e.target.value })} style={{ ...inp, width: "100%" }}>
              {Object.keys(CATEGORY_COLORS).map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 12, color: "#8b92a8", display: "block", marginBottom: 4 }}>Beskrivelse</label>
            <textarea placeholder="Valgfri..." value={modalData.description || ""} onChange={e => setModalData({ ...modalData, description: e.target.value })} style={{ ...inp, minHeight: 60, width: "100%" }} />
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 12, color: "#8b92a8", display: "block", marginBottom: 4 }}>Start</label>
              <DatePicker selected={modalData.start_time} onChange={d => setModalData({ ...modalData, start_time: d })} showTimeSelect dateFormat="yyyy-MM-dd HH:mm" />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 12, color: "#8b92a8", display: "block", marginBottom: 4 }}>Slut</label>
              <DatePicker selected={modalData.end_time} onChange={d => setModalData({ ...modalData, end_time: d })} showTimeSelect dateFormat="yyyy-MM-dd HH:mm" />
            </div>
          </div>
        </div>
        <div style={{ marginTop: 18, display: "flex", gap: 8, justifyContent: "flex-end" }}>
          {modalData.id && <button onClick={deleteEvent} style={{ ...btnDanger, marginRight: "auto" }}>Slet</button>}
          <button onClick={() => setModalOpen(false)} style={btnGhost}>Annuller</button>
          <button onClick={saveEvent} style={btnPrimary}>{modalData.id ? "Gem" : "Opret"}</button>
        </div>
      </Modal>

      {/* ───────── TURNERING MODAL ───────── */}
      <Modal isOpen={tModalOpen} onRequestClose={() => setTModalOpen(false)} ariaHideApp={false}
        style={{
          overlay: { background: "rgba(0,0,0,.7)", zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center" },
          content: {
            position: "relative", inset: "auto", maxWidth: 520, width: "90%",
            background: "#14171f", border: "1px solid #333", borderRadius: 12,
            color: "#fff", padding: 24, maxHeight: "90vh", overflowY: "auto"
          }
        }}>
        <h2>{tModalData.id ? "Rediger turnering" : "Ny turnering"}</h2>
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 12 }}>
          <input placeholder="Navn" value={tModalData.name || ""} onChange={e => setTModalData({ ...tModalData, name: e.target.value })} style={inp} />
          <input placeholder="Spil (fx CS2)" value={tModalData.game || ""} onChange={e => setTModalData({ ...tModalData, game: e.target.value })} style={inp} />
          <div style={{ display: "flex", gap: 10 }}>
            <select value={tModalData.status || "upcoming"} onChange={e => setTModalData({ ...tModalData, status: e.target.value })} style={{ ...inp, flex: 1 }}>
              {TOURNAMENT_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <input placeholder="Format" value={tModalData.format || ""} onChange={e => setTModalData({ ...tModalData, format: e.target.value })} style={{ ...inp, flex: 1 }} />
          </div>
          <div><label>Dato & tid: </label>
            <DatePicker selected={tModalData.date} onChange={d => setTModalData({ ...tModalData, date: d })} showTimeSelect dateFormat="yyyy-MM-dd HH:mm" /></div>
          <div style={{ display: "flex", gap: 10 }}>
            <input type="number" placeholder="Max deltagere" value={tModalData.max_participants || ""} onChange={e => setTModalData({ ...tModalData, max_participants: e.target.value })} style={{ ...inp, flex: 1 }} />
            <input type="number" placeholder="Gebyr (kr)" value={tModalData.fee ?? ""} onChange={e => setTModalData({ ...tModalData, fee: e.target.value })} style={{ ...inp, flex: 1 }} />
          </div>
          <input placeholder="Challengermode URL" value={tModalData.challengermode_url || ""} onChange={e => setTModalData({ ...tModalData, challengermode_url: e.target.value })} style={inp} />
          <div style={{ borderTop: "1px solid #333", paddingTop: 10, fontSize: 13, color: "#8b92a8" }}>Præmier (fri tekst)</div>
          <input placeholder="🥇 1. plads" value={tModalData.prize_1 || ""} onChange={e => setTModalData({ ...tModalData, prize_1: e.target.value })} style={inp} />
          <input placeholder="🥈 2. plads" value={tModalData.prize_2 || ""} onChange={e => setTModalData({ ...tModalData, prize_2: e.target.value })} style={inp} />
          <input placeholder="🥉 3. plads" value={tModalData.prize_3 || ""} onChange={e => setTModalData({ ...tModalData, prize_3: e.target.value })} style={inp} />
        </div>
        <div style={{ marginTop: 14, display: "flex", gap: 8 }}>
          <button onClick={saveTournament} style={btnPrimary}>{tModalData.id ? "Gem" : "Opret"}</button>
          <button onClick={() => setTModalOpen(false)} style={btnGhost}>Annuller</button>
        </div>
      </Modal>

      {/* ───────── DELTAGER MODAL ───────── */}
      <Modal isOpen={showParticipants !== null} onRequestClose={() => setShowParticipants(null)} ariaHideApp={false}
        style={{
          overlay: { background: "rgba(0,0,0,.7)", zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center" },
          content: {
            position: "relative", inset: "auto", maxWidth: 480, width: "90%",
            background: "#14171f", border: "1px solid #333", borderRadius: 12,
            color: "#fff", padding: 24, maxHeight: "90vh", overflowY: "auto"
          }
        }}>
        <h2>Tilmeldte spillere</h2>
        <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 12 }}>
          <thead><tr style={{ background: "#1c2030" }}><th style={th}>Spiller</th><th style={th}>Betalt</th><th style={th}>Tilmeldt</th></tr></thead>
          <tbody>
            {participants.map((p, i) => (
              <tr key={i} style={{ borderBottom: "1px solid #222" }}>
                <td style={td}>{p.username || p.steamid}</td>
                <td style={td}>{p.paid ? "✓" : "—"}</td>
                <td style={td}>{p.joined_at ? new Date(p.joined_at).toLocaleDateString("da-DK") : "-"}</td>
              </tr>
            ))}
            {participants.length === 0 && <tr><td style={td} colSpan={3}>Ingen tilmeldte endnu</td></tr>}
          </tbody>
        </table>
        <button onClick={() => setShowParticipants(null)} style={{ ...btnGhost, marginTop: 14 }}>Luk</button>
      </Modal>
    </div>
  );
}

// ── Styles ──
const th = { padding: 10, textAlign: "left", fontSize: 13, color: "#8b92a8" };
const td = { padding: 10, fontSize: 14 };
const sel = { padding: 6, borderRadius: 6, border: "1px solid #333", background: "#14171f", color: "#fff" };
const inp = { padding: 9, borderRadius: 6, border: "1px solid #333", background: "#0d0f14", color: "#fff", fontSize: 14 };
const btnPrimary = { padding: "9px 16px", background: "#e8b43a", color: "#0d0f14", border: "none", borderRadius: 7, fontWeight: 600, cursor: "pointer" };
const btnDanger = { padding: "7px 14px", background: "#ef444422", color: "#ef4444", border: "1px solid #ef444444", borderRadius: 7, cursor: "pointer" };
const btnGhost = { padding: "7px 14px", background: "transparent", color: "#8b92a8", border: "1px solid #333", borderRadius: 7, cursor: "pointer" };